/* global axios */

import endPoints from './endPoints';
import {
  clearCookiesOnLogout,
  deleteIndexedDBOnLogout,
} from '../store/utils/api';
import SessionStorage from 'shared/helpers/sessionStorage';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';

// Caché de estado de sesión para evitar llamar al backend en cada navegación.
// Se invalida automáticamente cuando recibimos 401 en cualquier request o al hacer logout.
let sessionCache = { checked: false, authenticated: false };

export const invalidateSessionCache = () => {
  sessionCache = { checked: false, authenticated: false };
};

export default {
  validityCheck() {
    const urlData = endPoints('validityCheck');
    return axios.get(urlData.url);
  },
  logout() {
    const urlData = endPoints('logout');
    const fetchPromise = new Promise((resolve, reject) => {
      axios
        .delete(urlData.url)
        .then(response => {
          invalidateSessionCache();
          deleteIndexedDBOnLogout();
          clearCookiesOnLogout();
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
    return fetchPromise;
  },

  // Verifica sesión contra el backend. Reemplaza el antiguo `hasAuthCookie()` que solo
  // miraba si existía una cookie del lado del cliente — algo trivialmente falsificable
  // copiando cookies desde otro navegador. Ahora el backend valida el token en DB.
  //
  // Capa 1 anti-cookie-theft — DOS checks complementarios:
  //
  //   1) sessionStorage debe contener el session_nonce. Si no → no autenticado.
  //      Esto frena el ataque del video en su raíz: la ventana incognito con
  //      cookie clonada NO tiene nonce, entonces ni siquiera se monta el shell
  //      del dashboard (el router guard redirige a login directamente). Antes
  //      de este fix, el shell se montaba y solo las APIs fallaban con 401,
  //      dando la falsa impresión de "entré aunque no vea datos".
  //
  //   2) Chequeo contra el backend (/auth/session) que la cookie sigue siendo
  //      válida en DB. Esto cubre cookies revocadas por el admin, tokens
  //      expirados, etc.
  //
  // Si (1) pasa pero (2) falla → cookie inválida → no autenticado.
  // Si (1) falla → ni siquiera consultamos backend (sin nonce, nada útil
  // pasa adelante). Retornamos false y el router manda al login.
  async isAuthenticated({ forceRefresh = false } = {}) {
    if (!forceRefresh && sessionCache.checked) {
      return sessionCache.authenticated;
    }

    // Check 1: nonce presente en sessionStorage
    const hasNonce = !!SessionStorage.get(SESSION_STORAGE_KEYS.SESSION_NONCE);
    if (!hasNonce) {
      sessionCache = { checked: true, authenticated: false };
      return false;
    }

    // Check 2: cookie válida en backend
    try {
      await axios.get('/auth/session', { skipAuthInterceptor: true });
      sessionCache = { checked: true, authenticated: true };
      return true;
    } catch (error) {
      sessionCache = { checked: true, authenticated: false };
      return false;
    }
  },

  // DEPRECATED: mantener temporalmente solo para módulos legacy que aún no se migraron.
  // Retorna `true` de forma sincrónica basándose en la última verificación conocida.
  // PREFERIR `isAuthenticated()` que siempre valida contra el backend.
  hasAuthCookie() {
    return sessionCache.authenticated;
  },

  // DEPRECATED: los tokens ya no son accesibles desde JS (cookie httpOnly).
  // Los módulos que llamaban esto para setear headers manualmente deben dejar que
  // el navegador envíe la cookie automáticamente via `withCredentials: true`.
  getAuthData() {
    return false;
  },
  profileUpdate({ displayName, avatar, ...profileAttributes }) {
    const formData = new FormData();
    Object.keys(profileAttributes).forEach(key => {
      const hasValue = profileAttributes[key] === undefined;
      if (!hasValue) {
        formData.append(`profile[${key}]`, profileAttributes[key]);
      }
    });
    formData.append('profile[display_name]', displayName || '');
    if (avatar) {
      formData.append('profile[avatar]', avatar);
    }
    return axios.put(endPoints('profileUpdate').url, formData);
  },

  profilePasswordUpdate({ currentPassword, password, passwordConfirmation }) {
    return axios.put(endPoints('profileUpdate').url, {
      profile: {
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation,
      },
    });
  },

  updateUISettings({ uiSettings }) {
    return axios.put(endPoints('profileUpdate').url, {
      profile: { ui_settings: uiSettings },
    });
  },

  updateAvailability(availabilityData) {
    return axios.post(endPoints('availabilityUpdate').url, {
      profile: { ...availabilityData },
    });
  },

  updateAutoOffline(accountId, autoOffline = false) {
    return axios.post(endPoints('autoOffline').url, {
      profile: { account_id: accountId, auto_offline: autoOffline },
    });
  },

  deleteAvatar() {
    return axios.delete(endPoints('deleteAvatar').url);
  },

  resetPassword({ email }) {
    const urlData = endPoints('resetPassword');
    return axios.post(urlData.url, { email });
  },

  requestOtp({ email, purpose = 'password_reset' }) {
    const urlData = endPoints('otpRequest');
    return axios.post(urlData.url, { email, purpose });
  },

  verifyOtp({ email, otp_code, purpose = 'password_reset' }) {
    const urlData = endPoints('otpVerify');
    return axios.post(urlData.url, { email, otp_code, purpose });
  },

  otpResetPassword({ email, password, password_confirmation }) {
    const urlData = endPoints('otpResetPassword');
    return axios.post(urlData.url, { email, password, password_confirmation });
  },

  getTwoFactorStatus() {
    return axios.get(endPoints('twoFactorShow').url);
  },
  setupTwoFactor() {
    return axios.post(endPoints('twoFactorSetup').url);
  },
  enableTwoFactor({ otp_code }) {
    return axios.post(endPoints('twoFactorEnable').url, { otp_code });
  },
  disableTwoFactor({ otp_code }) {
    return axios.post(endPoints('twoFactorDisable').url, { otp_code });
  },

  setActiveAccount({ accountId }) {
    const urlData = endPoints('setActiveAccount');
    return axios.put(urlData.url, {
      profile: {
        account_id: accountId,
      },
    });
  },
  resendConfirmation() {
    const urlData = endPoints('resendConfirmation');
    return axios.post(urlData.url);
  },
  resetAccessToken() {
    const urlData = endPoints('resetAccessToken');
    return axios.post(urlData.url);
  },
};
